/**
 * Sandbox session — the softmaze.at side of the contract shared with each
 * project's apps (vollgas: `packages/shared/src/sandbox/session.ts`; the
 * others carry an equivalent module). The demo shell (this site) creates and
 * edits the session; the apps, built in sandbox mode and served under
 * /demo/<project>/<app>/, read it on boot and persist their fixture data
 * next to it. Everything is sessionStorage on this origin, shared by the shell
 * and the app iframes: the session lasts as long as the browser tab is open,
 * survives role switches and reloads, and ends on Exit or tab close. No
 * server, no account. Keep the copies in sync.
 */
import { appBasePath, roleDef, type SandboxProject } from './sandboxProjects'

export type SandboxLang = 'de' | 'en'
export type SandboxTheme = 'light' | 'dark'
export type SandboxScenario = 'busy' | 'empty'

export interface SandboxSession {
  id: string
  createdAt: string
  expiresAt: string
  role: string
  lang: SandboxLang
  theme: SandboxTheme
  scenario: SandboxScenario
  shop: { name: string; city: string }
  dataVersion: number
}

/** No practical expiry — the tab's lifetime is the session. */
export const SANDBOX_TTL_MS = 3650 * 24 * 60 * 60 * 1000

export function parseSession(p: SandboxProject, raw: string | null): SandboxSession | null {
  if (!raw) return null
  try {
    const s = JSON.parse(raw) as Partial<SandboxSession>
    if (typeof s.id !== 'string' || !s.id) return null
    if (typeof s.expiresAt !== 'string' || Number.isNaN(Date.parse(s.expiresAt))) return null
    if (typeof s.role !== 'string' || !p.roles.some((r) => r.key === s.role)) return null
    return {
      id: s.id,
      createdAt: typeof s.createdAt === 'string' ? s.createdAt : new Date().toISOString(),
      expiresAt: s.expiresAt,
      role: s.role,
      lang: s.lang === 'en' ? 'en' : 'de',
      theme: s.theme === 'dark' ? 'dark' : 'light',
      scenario: s.scenario === 'empty' ? 'empty' : 'busy',
      shop: {
        name: typeof s.shop?.name === 'string' && s.shop.name ? s.shop.name : p.shop.name,
        city: typeof s.shop?.city === 'string' && s.shop.city ? s.shop.city : p.shop.city,
      },
      dataVersion: typeof s.dataVersion === 'number' ? s.dataVersion : 1,
    }
  } catch {
    return null
  }
}

export function isExpired(s: SandboxSession, now = Date.now()): boolean {
  return Date.parse(s.expiresAt) <= now
}

export function purgeSandboxStorage(p: SandboxProject, storage: Storage = sessionStorage): void {
  const doomed: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i)
    if (k && k.startsWith(p.storagePrefix)) doomed.push(k)
  }
  for (const k of doomed) storage.removeItem(k)
}

export function readSession(p: SandboxProject, storage: Storage = sessionStorage): SandboxSession | null {
  let s: SandboxSession | null = null
  try {
    s = parseSession(p, storage.getItem(p.sessionKey))
  } catch {
    return null
  }
  if (!s) return null
  if (isExpired(s)) {
    purgeSandboxStorage(p, storage)
    return null
  }
  return s
}

export function writeSession(p: SandboxProject, s: SandboxSession, storage: Storage = sessionStorage): void {
  storage.setItem(p.sessionKey, JSON.stringify(s))
}

function randomId(): string {
  const c = globalThis.crypto
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, '').slice(0, 16)
  return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10)
}

export function createSession(
  p: SandboxProject,
  overrides: Partial<Omit<SandboxSession, 'id' | 'createdAt' | 'expiresAt'>> = {},
  now = Date.now(),
): SandboxSession {
  const { shop, ...rest } = overrides
  return {
    id: randomId(),
    createdAt: new Date(now).toISOString(),
    expiresAt: new Date(now + SANDBOX_TTL_MS).toISOString(),
    role: p.defaultRole,
    lang: 'de',
    theme: 'light',
    scenario: 'busy',
    dataVersion: 1,
    ...rest,
    shop: { ...p.shop, ...(shop ?? {}) },
  }
}


/** Where a web role opens inside its app. */
export function roleUrl(p: SandboxProject, role: string): string {
  const r = roleDef(p, role)
  return `${appBasePath(p, r.app)}${r.home}`
}
